import { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { escapeTooltipHtml, formatTooltipValue, formatChartTooltipDateTime, getChartTooltipChrome } from "@/utils/chart-utils.js";
import { RcaTrendScoreSidebar } from "@/components/root-cause-analysis/rca-trend-score-sidebar.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import {
  RcaDriftStatsPanel,
  RcaTrendStatsPanel,
} from "@/components/root-cause-analysis/rca-trend-stats-panel.jsx";
import { cn } from "@/lib/utils.js";

/** Default Highcharts height for RCA measurement trend cards. */
export const CHART_HEIGHT_PX = 120;

/** List wrapper for stacked trend cards. */
export const RCA_TREND_LIST_CLASS = "flex list-none flex-col gap-3 p-0";

/** Single trend card shell. */
export const RCA_TREND_CARD_CLASS =
  "rounded-md border border-border bg-card p-1.5 shadow-sm sm:p-2";

/** Score ~4fr, chart ~9fr, stats ~2fr (stats gave ~⅓ width to score vs 1:3:1). */
export const RCA_TREND_ROW_CLASS =
  "grid min-w-0 grid-cols-[minmax(0,4fr)_minmax(0,9fr)_minmax(0,2fr)] items-stretch gap-2";

/**
 * @typedef {{ AssetTagId?: number, TagDescription?: string, UoM?: string|number, LoLo?: number, Lo?: number, Hi?: number, HiHi?: number, Min?: number, Max?: number, Avg?: number, StdDev?: number, ZScore?: number, Anomalies?: number, Measurements?: Array<{ DateTime: string, Value: number, TextValue?: string }> }} MeasurementWithStatsRow
 */

/**
 * @param {unknown} n
 */
export function formatOneDecimal(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toFixed(1);
}

/**
 * Count trend samples strictly above Hi or strictly below Lo (each point at most once).
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @returns {number}
 */
export function countTrendPointsOutsideLoHi(row) {
  const hi = Number(row.Hi);
  const lo = Number(row.Lo);
  const hasHi = Number.isFinite(hi);
  const hasLo = Number.isFinite(lo);
  if (!hasHi && !hasLo) return 0;

  const raw = Array.isArray(row?.Measurements) ? row.Measurements : [];
  let count = 0;
  for (const m of raw) {
    const y = Number(m.Value);
    if (!Number.isFinite(y)) continue;
    if ((hasHi && y > hi) || (hasLo && y < lo)) count += 1;
  }
  return count;
}

/** @typedef {{ t: number, y: number }} RcaTrendPoint */

/**
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @returns {RcaTrendPoint[]}
 */
export function getSortedTrendPointsFromRow(row) {
  const raw = Array.isArray(row?.Measurements) ? row.Measurements : [];
  return [...raw]
    .map((m) => ({
      t: new Date(m.DateTime).getTime(),
      y: Number(m.Value),
    }))
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.y))
    .sort((a, b) => a.t - b.t);
}

/**
 * @param {RcaTrendPoint[]} window
 * @returns {number[]} indices of local maxima within `window`
 */
function findLocalMaximaIndicesInWindow(window) {
  /** @type {number[]} */
  const indices = [];
  for (let i = 0; i < window.length; i++) {
    const y = window[i].y;
    const hasLeft = i > 0;
    const hasRight = i < window.length - 1;
    let isLocalMax = false;
    if (hasLeft && hasRight) {
      isLocalMax = y > window[i - 1].y && y > window[i + 1].y;
    } else if (hasLeft) {
      isLocalMax = y > window[i - 1].y;
    } else if (hasRight) {
      isLocalMax = y > window[i + 1].y;
    } else {
      isLocalMax = true;
    }
    if (isLocalMax) indices.push(i);
  }
  return indices;
}

/**
 * @param {RcaTrendPoint[]} window
 * @returns {number[]} indices of local minima within `window`
 */
function findLocalMinimaIndicesInWindow(window) {
  /** @type {number[]} */
  const indices = [];
  for (let i = 0; i < window.length; i++) {
    const y = window[i].y;
    const hasLeft = i > 0;
    const hasRight = i < window.length - 1;
    let isLocalMin = false;
    if (hasLeft && hasRight) {
      isLocalMin = y < window[i - 1].y && y < window[i + 1].y;
    } else if (hasLeft) {
      isLocalMin = y < window[i - 1].y;
    } else if (hasRight) {
      isLocalMin = y < window[i + 1].y;
    } else {
      isLocalMin = true;
    }
    if (isLocalMin) indices.push(i);
  }
  return indices;
}

/**
 * Last 3 samples: at each local minimum, project one step forward using slope from the prior point.
 * Near low band when any projection is below Lo.
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 */
export function isTrendNearLowAlarmBand(row) {
  const lo = Number(row.Lo);
  if (!Number.isFinite(lo)) return false;

  const points = getSortedTrendPointsFromRow(row);
  if (points.length < 2) return false;

  const window = points.slice(-3);
  if (window.length < 2) return false;

  const windowStartGlobal = points.length - window.length;
  const minima = findLocalMinimaIndicesInWindow(window);

  for (const i of minima) {
    const minPoint = window[i];
    const prevPoint =
      i > 0 ? window[i - 1] : (points[windowStartGlobal - 1] ?? null);
    if (!prevPoint) continue;

    const dt = minPoint.t - prevPoint.t;
    if (dt <= 0) continue;

    const slope = (minPoint.y - prevPoint.y) / dt;
    const projectedY = minPoint.y + slope * dt;
    if (projectedY < lo) return true;
  }

  return false;
}

/**
 * Last 3 samples: at each local maximum, project one step forward using slope from the prior point.
 * Near high band when any projection exceeds Hi.
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 */
export function isTrendNearHighAlarmBand(row) {
  const hi = Number(row.Hi);
  if (!Number.isFinite(hi)) return false;

  const points = getSortedTrendPointsFromRow(row);
  if (points.length < 2) return false;

  const window = points.slice(-3);
  if (window.length < 2) return false;

  const windowStartGlobal = points.length - window.length;
  const maxima = findLocalMaximaIndicesInWindow(window);

  for (const i of maxima) {
    const maxPoint = window[i];
    const prevPoint =
      i > 0 ? window[i - 1] : (points[windowStartGlobal - 1] ?? null);
    if (!prevPoint) continue;

    const dt = maxPoint.t - prevPoint.t;
    if (dt <= 0) continue;

    const slope = (maxPoint.y - prevPoint.y) / dt;
    const projectedY = maxPoint.y + slope * dt;
    if (projectedY > hi) return true;
  }

  return false;
}

/**
 * True when the trend's last-three-point projection suggests approach toward Lo or Hi.
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 */
export function isLastPointInOrApproachingAlarmBand(row) {
  return isTrendNearLowAlarmBand(row) || isTrendNearHighAlarmBand(row);
}

/**
 * Which alarm band groups to draw: low and/or high sides when the trend is approaching
 * that limit (last-three-point projection) or when any sample breached Lo / Hi in the window.
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @returns {{ showLow: boolean, showHigh: boolean }}
 */
export function getTrendAlarmBandSides(row) {
  const lo = Number(row.Lo);
  const hi = Number(row.Hi);
  const hasLo = Number.isFinite(lo);
  const hasHi = Number.isFinite(hi);

  let anyBelowLo = false;
  let anyAboveHi = false;
  if (hasLo || hasHi) {
    for (const p of getSortedTrendPointsFromRow(row)) {
      if (hasLo && p.y < lo) anyBelowLo = true;
      if (hasHi && p.y > hi) anyAboveHi = true;
      if (anyBelowLo && anyAboveHi) break;
    }
  }

  return {
    showLow: isTrendNearLowAlarmBand(row) || anyBelowLo,
    showHigh: isTrendNearHighAlarmBand(row) || anyAboveHi,
  };
}

/**
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @returns {boolean}
 */
export function hasPlottableMeasurements(row) {
  const raw = Array.isArray(row?.Measurements) ? row.Measurements : [];
  return raw.some((m) => {
    const t = new Date(m.DateTime).getTime();
    const y = Number(m.Value);
    return Number.isFinite(t) && Number.isFinite(y);
  });
}

/**
 * @param {MeasurementWithStatsRow} row
 * @param {string} lineColor
 * @param {{ showAlarmLimits?: boolean, isDark?: boolean }} [chartExtras] When false, alarm bands are omitted. When true, draws LoLo/Lo and/or Hi/HiHi bands for sides the trend is approaching or has breached (Lo/Hi) in the window.
 */
export function buildChartOptions(row, lineColor, chartExtras) {
  const showAlarmLimits = chartExtras?.showAlarmLimits !== false;
  const isDark = chartExtras?.isDark === true;
  const tagLabel =
    String(row.TagDescription ?? "").trim() || `Tag ${row.AssetTagId}`;

  const raw = Array.isArray(row.Measurements) ? row.Measurements : [];
  const sorted = [...raw]
    .map((m) => ({
      t: new Date(m.DateTime).getTime(),
      y: Number(m.Value),
      textValue: m.TextValue,
    }))
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.y))
    .sort((a, b) => a.t - b.t);

  const seriesData = sorted.map((p) => {
    if (p.textValue) {
      return {
        x: p.t,
        y: p.y,
        custom: { textValue: p.textValue },
      };
    }
    return [p.t, p.y];
  });

  const avgVal = Number(row.Avg);
  const avgPlotLines = Number.isFinite(avgVal)
    ? [
        {
          value: avgVal,
          color: isDark
            ? "rgba(255, 255, 255, 0.45)"
            : "rgba(5, 7, 10, 0.5)",
          dashStyle: "ShortDash",
          width: 1.5,
          zIndex: 5,
        },
      ]
    : [];

  const lolo = Number(row.LoLo);
  const lo = Number(row.Lo);
  const hi = Number(row.Hi);
  const hihi = Number(row.HiHi);

  const ys = sorted.map((p) => p.y);
  const rMin = Number(row.Min);
  const rMax = Number(row.Max);

  let axisMin = ys.length ? Math.min(...ys) : NaN;
  let axisMax = ys.length ? Math.max(...ys) : NaN;
  if (Number.isFinite(rMin)) {
    axisMin = Number.isFinite(axisMin) ? Math.min(axisMin, rMin) : rMin;
  }
  if (Number.isFinite(rMax)) {
    axisMax = Number.isFinite(axisMax) ? Math.max(axisMax, rMax) : rMax;
  }
  const alarmBandSides = showAlarmLimits
    ? getTrendAlarmBandSides(row)
    : { showLow: false, showHigh: false };
  const { showLow: showLowAlarmBands, showHigh: showHighAlarmBands } =
    alarmBandSides;

  if (showLowAlarmBands) {
    for (const v of [lolo, lo]) {
      if (Number.isFinite(v)) {
        axisMin = Number.isFinite(axisMin) ? Math.min(axisMin, v) : v;
      }
    }
  }
  if (showHighAlarmBands) {
    for (const v of [hi, hihi]) {
      if (Number.isFinite(v)) {
        axisMax = Number.isFinite(axisMax) ? Math.max(axisMax, v) : v;
      }
    }
  }
  if (Number.isFinite(avgVal)) {
    axisMin = Number.isFinite(axisMin) ? Math.min(axisMin, avgVal) : avgVal;
    axisMax = Number.isFinite(axisMax) ? Math.max(axisMax, avgVal) : avgVal;
  }
  if (!Number.isFinite(axisMin)) axisMin = 0;
  if (!Number.isFinite(axisMax)) axisMax = axisMin + 1;

  const span = Math.max(axisMax - axisMin, 1e-9);
  const pad = Math.max(span * 0.06, 1e-6);
  const yMin = axisMin - pad;
  const yMax = axisMax + pad;

  const alarmRed = isDark
    ? "rgba(239, 68, 68, 0.22)"
    : "rgba(254, 202, 202, 0.35)";
  /** LoLo–Lo and Hi–HiHi warning wash (rgba so bands stay translucent over the chart) */
  const alarmWarningBand = isDark
    ? "rgba(255, 214, 51, 0.3)"
    : "rgba(241, 232, 108, 0.45)";

  /** @type {Array<{ from: number, to: number, color: string, zIndex: number }>} */
  const plotBands = [];

  if (showLowAlarmBands && Number.isFinite(lolo)) {
    plotBands.push({
      from: yMin,
      to: lolo,
      color: alarmRed,
      zIndex: 0,
    });
  }
  if (showLowAlarmBands && Number.isFinite(lo)) {
    const orangeLowFrom = Number.isFinite(lolo) ? lolo : yMin;
    if (lo > orangeLowFrom) {
      plotBands.push({
        from: orangeLowFrom,
        to: lo,
        color: alarmWarningBand,
        zIndex: 0,
      });
    }
  }

  if (showHighAlarmBands && Number.isFinite(hihi)) {
    plotBands.push({
      from: hihi,
      to: yMax,
      color: alarmRed,
      zIndex: 0,
    });
  }
  if (showHighAlarmBands && Number.isFinite(hi)) {
    const orangeHighTo = Number.isFinite(hihi) ? hihi : yMax;
    if (orangeHighTo > hi) {
      plotBands.push({
        from: hi,
        to: orangeHighTo,
        color: alarmWarningBand,
        zIndex: 0,
      });
    }
  }

  return {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: CHART_HEIGHT_PX,
      spacing: [4, 4, 2, 4],
    },
    time: {
      useUTC: false,
    },
    title: { text: null },
    xAxis: {
      type: "datetime",
      title: { text: null },
      labels: {
        style: {
          fontSize: "10px",
          color: "hsl(var(--muted-foreground))",
        },
      },
      gridLineColor: "hsl(var(--border))",
    },
    yAxis: {
      title: { text: null },
      min: yMin,
      max: yMax,
      labels: {
        style: {
          fontSize: "10px",
          color: "hsl(var(--muted-foreground))",
        },
      },
      gridLineColor: "hsl(var(--border))",
      plotBands: plotBands.length ? plotBands : undefined,
      plotLines: avgPlotLines,
    },
    tooltip: {
      ...getChartTooltipChrome(),
      borderRadius: 8,
      borderWidth: 1,
      shadow: true,
      useHTML: true,
      hideDelay: 0,
      formatter: function () {
        const formattedValue = formatTooltipValue(this.y);
        const textValue =
          this.point?.custom?.textValue
          ?? this.point?.options?.custom?.textValue;
        const textValueBlock = textValue
          ? `<p style="font-size: 13px; font-weight: 400; color: hsl(var(--muted-foreground)); margin-top: 6px;">${escapeTooltipHtml(String(textValue))}</p>`
          : "";
        return `
          <div style="padding: 10px;">
            <p style="font-size: 11px; color: hsl(var(--muted-foreground)); margin-bottom: 4px;">${escapeTooltipHtml(tagLabel)}</p>
            <p style="font-size: 11px; color: hsl(var(--muted-foreground)); margin-bottom: 4px;">${formatChartTooltipDateTime(this.x)}</p>
            <p style="font-size: 16px; font-weight: 500; color: hsl(var(--foreground));">${formattedValue}</p>
            ${textValueBlock}
          </div>
        `;
      },
    },
    plotOptions: {
      line: {
        lineWidth: 2,
        marker: {
          enabled: sorted.length < 48,
          states: {
            hover: {
              enabled: true,
              radius: 4,
            },
          },
        },
      },
    },
    series: [
      {
        name: tagLabel,
        data: seriesData,
        color: lineColor,
        zIndex: 3,
      },
    ],
    credits: { enabled: false },
    legend: { enabled: false },
  };
}

/**
 * Tag name and series color swatch (left column of {@link RcaTrendCardRow}).
 *
 * @param {{ lineColor: string, label: import("react").ReactNode, uom: string }} props
 */
export function RcaTrendCardTitle({ lineColor, label, uom }) {
  return (
    <div className='flex min-w-0 items-start gap-1.5'>
      <span
        className='mt-0.5 h-2 w-2 shrink-0 rounded-full'
        style={{ backgroundColor: lineColor }}
        aria-hidden
      />
      <span className='min-w-0 break-words text-xs font-medium leading-snug text-foreground'>
        {label}
        {uom ? (
          <span className='font-light text-muted-foreground'> ({uom})</span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * Trend card body: title + optional score (~1/5), chart (~3/5), header stats (~1/5).
 *
 * @param {{
 *   row: MeasurementWithStatsRow|Record<string, unknown>,
 *   lineColor: string,
 *   label: import("react").ReactNode,
 *   uom: string,
 *   useAlarmLimits?: boolean,
 *   score?: import("@/components/root-cause-analysis/rca-key-trend-score.js").KeyTrendScoreBreakdown,
 * }} props
 */
export function RcaTrendCardRow({
  row,
  lineColor,
  label,
  uom,
  useAlarmLimits = false,
  score,
}) {
  return (
    <div className={RCA_TREND_ROW_CLASS}>
      <div className='flex min-w-0 flex-col justify-center border-r border-border pr-2'>
        {score ? (
          <RcaTrendScoreSidebar
            row={row}
            lineColor={lineColor}
            label={label}
            uom={uom}
            score={score}
            useAlarmLimits={useAlarmLimits}
          />
        ) : (
          <RcaTrendCardTitle lineColor={lineColor} label={label} uom={uom} />
        )}
      </div>
      <div className='flex min-h-0 min-w-0 items-center self-stretch'>
        <SensorTrendChart
          row={row}
          lineColor={lineColor}
          showAlarmLimits={useAlarmLimits}
        />
      </div>
      <div className='flex min-w-0 flex-col justify-center border-l border-border pl-2'>
        <RcaTrendStatsPanel row={row} useAlarmLimits={useAlarmLimits} />
      </div>
    </div>
  );
}

/**
 * @param {{ row: MeasurementWithStatsRow, lineColor: string, showAlarmLimits?: boolean }} props
 */
export function SensorTrendChart({ row, lineColor, showAlarmLimits = true }) {
  const { theme } = useTheme();
  const hasData = useMemo(() => hasPlottableMeasurements(row), [row]);
  const options = useMemo(
    () =>
      hasData
        ? buildChartOptions(row, lineColor, {
            showAlarmLimits,
            isDark: theme === "dark",
          })
        : null,
    [row, lineColor, hasData, showAlarmLimits, theme]
  );

  if (!hasData || !options) {
    return null;
  }

  return (
    <div
      className='w-full min-w-0'
      aria-label={`Trend chart for ${String(row.TagDescription ?? row.AssetTagId)}`}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
}
